import { createSlice } from '@reduxjs/toolkit'
import { toast } from 'utils/notify'
import { chunk } from 'lodash'
import { FirestoreDatabase, storageRef } from '../index'
import { Collection } from '../constants'
import moment from 'moment/moment'
import { cloudFunctionsInstance } from '../config/axios'
import { signOut } from './auth'

const POSTS_PAGE_SIZE = 20

// Initial state for community posts
const initialState = {
  loadingState: {},
  posts: [],
  post: {},
  postComments: [],
  postLikes: [],
  postTopics: [],
  postFlags: [],
  commentReplies: {},
  inProgress: false,
  searchQuery: '',
  hasMorePosts: true
}

// Create community slice
const communitySlice = createSlice({
  name: 'community',
  initialState,
  reducers: {
    setLoadingState: (state, action) => {
      state.loadingState = {
        ...state.loadingState,
        ...action.payload
      }
    },
    postTopicsSuccess: (state, action) => {
      state.postTopics = action.payload
    },
    // Replace the full posts list (used when filters change and pagination resets)
    communityPostsSuccess: (state, action) => {
      state.posts = action.payload || []
    },
    // Merge a page's live snapshot into posts, keyed by page index so pages don't clobber each other
    setCommunityPostsPage: (state, action) => {
      const { pageIndex, posts } = action.payload
      const pageStart = pageIndex * POSTS_PAGE_SIZE
      const before = state.posts.slice(0, pageStart)
      const after = state.posts.slice(pageStart + POSTS_PAGE_SIZE)
      state.posts = [...before, ...posts, ...after]
    },
    setHasMorePosts: (state, action) => {
      state.hasMorePosts = action.payload
    },
    setPost: (state, action) => {
      state.post = action.payload
    },
    setPostLikes(state, action) {
      state.postLikes = action.payload
    },
    setPostComments(state, action) {
      state.postComments = action.payload
    },
    setPostFlags(state, action) {
      state.postFlags = action.payload
    },
    setCommentReplies(state, action) {
      const { commentId, replies } = action.payload
      state.commentReplies = {
        ...state.commentReplies,
        [commentId]: replies
      }
    },
    resetPost: (state) => {
      state.postComments = []
      state.post = {}
      state.postLikes = []
      state.postFlags = []
      state.commentReplies = {}
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload
    }
  },
  extraReducers: {
    [signOut.fulfilled]: (state) => {
      state.loadingState = {}
      state.posts = []
      state.post = {}
      state.postComments = []
      state.postLikes = []
      state.postTopics = []
      state.postFlags = []
      state.commentReplies = {}
      state.inProgress = false
      state.searchQuery = ''
      state.hasMorePosts = true
    }
  }
})

// Export actions
export const { postTopicsSuccess, communityPostsSuccess, setSearchQuery } = communitySlice.actions

export const fetchPostTopics = (conferenceId) => async (dispatch) => {
  try {
    const topicsSnapshot = await FirestoreDatabase.collection(Collection.POST_TOPICS)
      .where('conferenceId', '==', conferenceId)
      .get()

    const topics = topicsSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data()
    }))
    const sortedTopics = topics.sort((a, b) => a.title.localeCompare(b.title))
    dispatch(postTopicsSuccess(sortedTopics))
  } catch (err) {
    console.error('Error fetching post topics:', err)
    toast.error('Could not load topics. Try again.')
  }
}

export const addPostTopic = (payload, conferenceId) => async (dispatch) => {
  try {
    await FirestoreDatabase.collection(Collection.POST_TOPICS).add(payload)
    await dispatch(fetchPostTopics(conferenceId))
  } catch (err) {
    console.error('Error adding post topic:', err)
    toast.error('Could not add topic. Try again.')
  }
}

export const updatePostTopic = (topicId, payload) => async (dispatch, getState) => {
  try {
    const postTopics = getState().community.postTopics
    await FirestoreDatabase.collection(Collection.POST_TOPICS).doc(topicId).update(payload)
    const updatedPostTopics = postTopics.map((topic) => {
      if (topic.id === topicId) {
        return { ...topic, ...payload }
      }
      return topic
    })
    dispatch(postTopicsSuccess(updatedPostTopics))
  } catch (err) {
    console.error('Error updating post topic:', err)
    toast.error('Could not update topic. Try again.')
  }
}

export const deletePostTopic = (topicId) => async (dispatch, getState) => {
  try {
    // First, check if the topic is used in any posts
    const postsSnapshot = await FirestoreDatabase.collection(Collection.COMMUNITY_POSTS)
      .where('topics', 'array-contains', topicId)
      .get()

    if (!postsSnapshot.empty) {
      toast.warning('Topic is in use. Remove it from posts before deleting.')
      return
    }

    const postTopics = getState().community.postTopics
    await FirestoreDatabase.collection(Collection.POST_TOPICS).doc(topicId).delete()
    const updatedPostTopics = postTopics.filter((topic) => topic.id !== topicId)
    dispatch(postTopicsSuccess(updatedPostTopics))
    toast.success('Topic deleted.')
  } catch (err) {
    console.error('Error deleting post topic:', err)
  }
}

const enrichPost = (doc, { user, attendees, topics }) => {
  const data = doc.data()
  const post = {
    id: doc.id,
    ...data
  }

  if (post?.isCreatedByAdmin) {
    post.author = {
      name: `Admin ${user?.id === post?.createdBy ? ' (You)' : ''}`
    }
  } else {
    const attendee = attendees.find((a) => a.id === post?.createdBy)
    post.author = {
      name: attendee?.id ? `${attendee?.firstName} ${attendee?.lastName}` : '[Deleted User]',
      email: attendee?.email,
      profilePicture: attendee?.imageName,
      position: attendee?.position,
      role: attendee?.role
    }
  }

  if (post?.topics?.length) {
    post.topics = post.topics.map((topicId) => topics.find((topic) => topic.id === topicId) || {})
  }

  return post
}

// Builds the base filtered/ordered posts query shared by the first page and "load more"
const buildCommunityPostsQuery = (conferenceId, { filterType, topicIds } = {}) => {
  let query = FirestoreDatabase.collection(Collection.COMMUNITY_POSTS).where(
    'conferenceId',
    '==',
    conferenceId
  )

  if (filterType === 'pinned') {
    query = query.where('isPinned', '==', true)
  } else if (filterType === 'flagged') {
    query = query.where('isFlagged', '==', true)
  }

  if (topicIds?.length) {
    // Firestore array-contains-any supports at most 10 values
    query = query.where('topics', 'array-contains-any', topicIds.slice(0, 10))
  }

  return query.orderBy('createdAt', 'desc')
}

const fetchAttendeesAndTopics = async (conferenceId) => {
  const [attendeesSnapshot, topicsSnapshot] = await Promise.all([
    FirestoreDatabase.collection(Collection.ATTENDEES)
      .where('conferenceId', '==', conferenceId)
      .get(),
    FirestoreDatabase.collection(Collection.POST_TOPICS)
      .where('conferenceId', '==', conferenceId)
      .get()
  ])

  return {
    attendees: attendeesSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
    topics: topicsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
  }
}

// Loads the first page of posts (live) and resets pagination. Call again when filters/topics/conference change.
export const listenToCommunityPosts =
  (conferenceId, filters = {}) =>
  (dispatch, getState) => {
    if (!conferenceId) return
    const user = getState().auth.user
    dispatch(communitySlice.actions.setLoadingState({ posts: true }))
    dispatch(communityPostsSuccess([]))
    dispatch(communitySlice.actions.setHasMorePosts(true))

    const query = buildCommunityPostsQuery(conferenceId, filters).limit(POSTS_PAGE_SIZE)

    return fetchAttendeesAndTopics(conferenceId)
      .then(({ attendees, topics }) => {
        dispatch(postTopicsSuccess(topics))

        const unsubscribe = query.onSnapshot((snapshot) => {
          const posts = snapshot.docs.map((doc) => enrichPost(doc, { user, attendees, topics }))

          dispatch(communitySlice.actions.setCommunityPostsPage({ pageIndex: 0, posts }))
          dispatch(communitySlice.actions.setHasMorePosts(posts.length === POSTS_PAGE_SIZE))
          dispatch(communitySlice.actions.setLoadingState({ posts: false }))
        })

        return unsubscribe
      })
      .catch((error) => {
        console.error('Error setting up real-time listener for posts:', error)
        toast.error('Could not load community posts. Try again.')
      })
  }

// Loads the next page of posts (also live) after the last currently-loaded post
export const loadMoreCommunityPosts =
  (conferenceId, filters = {}, pageIndex, lastPost) =>
  (dispatch, getState) => {
    if (!conferenceId || !lastPost) return
    const user = getState().auth.user
    dispatch(communitySlice.actions.setLoadingState({ moreposts: true }))

    const query = buildCommunityPostsQuery(conferenceId, filters)
      .startAfter(lastPost.createdAt)
      .limit(POSTS_PAGE_SIZE)

    return fetchAttendeesAndTopics(conferenceId)
      .then(({ attendees, topics }) => {
        const unsubscribe = query.onSnapshot((snapshot) => {
          const posts = snapshot.docs.map((doc) => enrichPost(doc, { user, attendees, topics }))

          dispatch(communitySlice.actions.setCommunityPostsPage({ pageIndex, posts }))
          dispatch(communitySlice.actions.setHasMorePosts(posts.length === POSTS_PAGE_SIZE))
          dispatch(communitySlice.actions.setLoadingState({ moreposts: false }))
        })

        return unsubscribe
      })
      .catch((error) => {
        console.error('Error loading more community posts:', error)
        toast.error('Could not load more posts. Try again.')
      })
  }

// Create post action
export const createCommunityPost = async (postData, conferenceId) => {
  try {
    const postsRef = FirestoreDatabase.collection(Collection.COMMUNITY_POSTS)
    await postsRef.add({
      ...postData,
      createdAt: moment().utc().valueOf(),
      updatedAt: moment().utc().valueOf(),
      conferenceId: conferenceId
    })

    // await dispatch(fetchCommunityPosts(conferenceId))
  } catch (error) {
    console.error('Error creating community post:', error)
  }
}

// Update post action
export const updateCommunityPost = async (postId, postData) => {
  try {
    const postRef = FirestoreDatabase.collection('community-posts').doc(postId)
    await postRef.update({
      ...postData,
      updatedAt: moment().utc().valueOf()
    })
    toast.success('Post updated.')
  } catch (error) {
    console.error('Error updating community post:', error)
    toast.error('Could not update post. Try again.')
  }
}

export const pinOrUnpinPost = async (postId, { isPinned }) => {
  try {
    await FirestoreDatabase.collection(Collection.COMMUNITY_POSTS).doc(postId).update({
      isPinned: isPinned
    })
    toast.success(`Post ${isPinned ? 'pinned' : 'unpinned'} successfully`)
  } catch (error) {
    console.error('Error updating community post:', error)
    toast.error('Could not change pin status. Try again.')
  }
}

export const sendPinOrUnpinPostNotification = async (postId) => {
  try {
    await cloudFunctionsInstance.post('/notifyPostPinOrUnpin', { postId })
  } catch (err) {
    const errorMessage =
      err?.response?.data?.msg || 'Failed to send notification for pin/unpin post.'
    toast.error(errorMessage)
  }
}

// Delete post action
export const deleteCommunityPost = async (postId) => {
  try {
    const postRef = FirestoreDatabase.collection(Collection.COMMUNITY_POSTS).doc(postId)

    // Fetch post data to check for attachments
    const postDoc = await postRef.get()
    const postData = postDoc.data()

    // Delete attachment from storage if exists
    if (postData?.attachments?.length > 0) {
      for (const attachment of postData.attachments) {
        try {
          const relativePath = attachment.path.replace('images/', '')
          await storageRef.child(relativePath).delete()
        } catch (err) {
          console.error('Failed to delete attachment from storage:', err)
        }
      }
    }

    // Delete all comments in batches of 500 (Firestore batch limit)
    const commentsRef = postRef.collection('comments')
    const commentsSnapshot = await commentsRef.get()
    if (!commentsSnapshot.empty) {
      const comments = commentsSnapshot.docs
      const batchSize = 500
      const batches = chunk(comments, batchSize)

      for (const batchComments of batches) {
        const batch = FirestoreDatabase.batch()
        batchComments.forEach((comment) => {
          batch.delete(comment.ref)
        })
        await batch.commit()
      }
    }

    // Now delete the post itself
    await postRef.delete()
  } catch (error) {
    console.error('Error deleting community post:', error)
  }
}

export const listenToSinglePost = (postId) => async (dispatch, getState) => {
  const user = getState().auth.user
  dispatch(communitySlice.actions.setLoadingState({ post: true }))

  const postRef = FirestoreDatabase.collection(Collection.COMMUNITY_POSTS).doc(postId)

  // Fetch attendees once for enrichment
  const attendeesSnapshot = await FirestoreDatabase.collection(Collection.ATTENDEES)
    .where('conferenceId', '==', user.conferenceId)
    .get()
  const attendees = attendeesSnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data()
  }))

  // Fetch topics once (or keep live if needed)
  const topicsSnapshot = await FirestoreDatabase.collection(Collection.POST_TOPICS)
    .where('conferenceId', '==', user.conferenceId)
    .get()
  const topics = topicsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
  dispatch(postTopicsSuccess(topics))

  // === 🔁 Listen to Post (incl. likes) ===
  const unsubscribePost = postRef.onSnapshot(async (doc) => {
    if (!doc.exists) {
      toast.warning(`Post not found or already deleted.`)
      return
    }

    const postData = { id: doc.id, ...doc.data() }

    try {
      // Author
      let author = {}
      if (postData.isCreatedByAdmin) {
        author = {
          name: `Admin ${user?.id === postData?.createdBy ? ' (You)' : ''}`
        }
      } else {
        const attendee = attendees.find((a) => a.id === postData?.createdBy)
        author = attendee
          ? {
              name: `${attendee.firstName} ${attendee.lastName}`,
              email: attendee.email,
              profilePicture: attendee.imageName || null,
              position: attendee.position,
              role: attendee.role
            }
          : { name: '[Deleted User]' }
      }

      // Topics
      const enrichedTopics =
        postData?.topics?.map((topicId) => {
          return topics.find((topic) => topic.id === topicId) || {}
        }) || []

      // Likes
      const likes = postData?.likedBy
        ?.map((item) => {
          const attendee = attendees.find((a) => a.id === item)
          return attendee
            ? {
                id: attendee.id,
                name: `${attendee.firstName} ${attendee.lastName}`,
                email: attendee.email,
                profilePicture: attendee.imageName,
                credential: attendee.credential,
                position: attendee.position,
                role: attendee.role
              }
            : { name: '[Deleted User]' }
        })
        .filter(Boolean)

      dispatch(communitySlice.actions.setPost({ ...postData, author, topics: enrichedTopics }))
      dispatch(communitySlice.actions.setPostLikes(likes))
      dispatch(communitySlice.actions.setLoadingState({ post: false }))
    } catch (error) {
      console.error('Error enriching post data:', error)
    }
  })

  return unsubscribePost
}

export const listenToComments = (postId) => async (dispatch, getState) => {
  const user = getState().auth.user
  dispatch(communitySlice.actions.setLoadingState({ postComments: true }))
  const attendeesSnapshot = await FirestoreDatabase.collection(Collection.ATTENDEES)
    .where('conferenceId', '==', user.conferenceId)
    .get()
  const attendees = attendeesSnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data()
  }))

  const commentsRef = FirestoreDatabase.collection(Collection.COMMUNITY_POSTS)
    .doc(postId)
    .collection('comments')
    .orderBy('createdAt', 'desc')

  const unsubscribe = commentsRef.onSnapshot((snapshot) => {
    const comments = snapshot.docs.map((doc) => {
      const data = doc.data()
      const attendee = attendees.find((a) => a.id === data.userId)

      // Enrich likedBy with attendee details
      const enrichedLikedBy = (data.likedBy || [])
        .map((attendeeId) => {
          const liker = attendees.find((a) => a.id === attendeeId)
          return liker
            ? {
                id: liker.id,
                name: `${liker.firstName} ${liker.lastName}`,
                profilePicture: liker.imageName,
                position: liker.position
              }
            : null
        })
        .filter(Boolean)

      return {
        id: doc.id,
        ...data,
        likedBy: enrichedLikedBy,
        author: attendee
          ? {
              name: `${attendee.firstName} ${attendee.lastName}`,
              email: attendee.email,
              profilePicture: attendee.imageName,
              position: attendee.position,
              role: attendee.role
            }
          : { name: '[Deleted User]' }
      }
    })

    dispatch(communitySlice.actions.setPostComments(comments))
    dispatch(communitySlice.actions.setLoadingState({ postComments: false }))
  })

  return unsubscribe
}

export const listenToReplies = (postId, commentId) => async (dispatch, getState) => {
  const user = getState().auth.user
  const attendeesSnapshot = await FirestoreDatabase.collection(Collection.ATTENDEES)
    .where('conferenceId', '==', user.conferenceId)
    .get()
  const attendees = attendeesSnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data()
  }))

  const repliesRef = FirestoreDatabase.collection(Collection.COMMUNITY_POSTS)
    .doc(postId)
    .collection('comments')
    .doc(commentId)
    .collection('replies')
    .orderBy('createdAt', 'desc')

  const unsubscribe = repliesRef.onSnapshot((snapshot) => {
    const replies = snapshot.docs.map((doc) => {
      const data = doc.data()
      const attendee = attendees.find((a) => a.id === data.userId)

      const enrichedLikedBy = (data.likedBy || [])
        .map((attendeeId) => {
          const liker = attendees.find((a) => a.id === attendeeId)
          return liker
            ? {
                id: liker.id,
                name: `${liker.firstName} ${liker.lastName}`,
                profilePicture: liker.imageName,
                position: liker.position
              }
            : null
        })
        .filter(Boolean)

      return {
        id: doc.id,
        ...data,
        likedBy: enrichedLikedBy,
        author: attendee
          ? {
              name: `${attendee.firstName} ${attendee.lastName}`,
              email: attendee.email,
              profilePicture: attendee.imageName,
              position: attendee.position,
              role: attendee.role
            }
          : { name: '[Deleted User]' }
      }
    })

    dispatch(communitySlice.actions.setCommentReplies({ commentId, replies }))
  })

  return unsubscribe
}

export const listenToPostIfFlagged = (postId) => async (dispatch, getState) => {
  const user = getState().auth.user
  dispatch(communitySlice.actions.setLoadingState({ postFlags: true }))
  const attendeesSnapshot = await FirestoreDatabase.collection(Collection.ATTENDEES)
    .where('conferenceId', '==', user.conferenceId)
    .get()
  const attendees = attendeesSnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data()
  }))

  const flaggedPostsRef = FirestoreDatabase.collection(Collection.FLAGGED_POSTS)
    .where('postId', '==', postId)
    .orderBy('createdAt', 'desc')

  const unsubscribe = flaggedPostsRef.onSnapshot((snapshot) => {
    const flags = snapshot.docs.map((doc) => {
      const data = doc.data()
      const attendee = attendees.find((a) => a.id === data?.flaggedMarkedById)

      return {
        id: doc.id,
        ...data,
        flaggedBy: attendee
          ? {
              name: `${attendee.firstName} ${attendee.lastName}`,
              email: attendee.email,
              profilePicture: attendee.imageName,
              position: attendee.position,
              role: attendee.role
            }
          : { name: '[Deleted User]' }
      }
    })

    dispatch(communitySlice.actions.setPostFlags(flags))
    dispatch(communitySlice.actions.setLoadingState({ postFlags: false }))
  })

  return unsubscribe
}

// Export reducer
export default communitySlice
